import {LVDataLayerHelper} from "./LVDataLayerHelper";
import {CommonHelpers} from "@falcon/services";

declare global {
    interface Window {
        adobeDataLayer: any;
    }
}

export interface CustomerAdobeDataLayerDetails {
    emailAddress?: string;
    mobileNumber?: string;
    postcode?: string;
}

export const pushToAdobeDataLayer = (): void => {

    const key = "adobeDataLayer";
    const storedData = sessionStorage.getItem(key);

    if (!storedData) { return; }

    const values = safelyParseJSON(storedData);
    const interaction = values.interaction;

    new LVDataLayerHelper().mapInteractionToDataLayer(interaction);

    (window as any).adobeDataLayer = (window as any).adobeDataLayer || [];

    (window as any).adobeDataLayer.push({
        event: values.event || "",
        quoteInfo: removeNullValues((window as any).LvDataLayer || {})
    });


    sessionStorage.removeItem(key);

    function safelyParseJSON(data: string | null): Record<string, any> {
        try {
            return data ? JSON.parse(data) : {};
        } catch {
            console.warn("Invalid JSON in sessionStorage:", key);
            return {};
        }
    }

    function removeNullValues(obj: any): any {
        if (typeof obj !== "object" || obj === null) { return obj; }

        return Array.isArray(obj)
            ? obj.map(removeNullValues).filter(item => item != null)
            : Object.fromEntries(
                Object.entries(obj)
                    .map(([k, v]) => [k, removeNullValues(v)])
                    .filter(([_, v]) => v != null)
            );
    }
};

export function pushToAdobeDataLayerDelayed(delay: number): any {
    return setTimeout(() => {
        pushToAdobeDataLayer();
    }, delay);
}

const normalizeEmailAddress = (emailAddress: string): string =>
    emailAddress.trim().toLowerCase();
const normalizePostcode = (postcode: string): string =>
    postcode.trim();
const hashSha256 = async (value: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
        .map(byte => byte.toString(16).padStart(2, "0"))
        .join("");
};
export async function putCustomerDetailsIntoAdobeDataLayer(
    details: CustomerAdobeDataLayerDetails
): Promise<void> {
    const emailAddress = details.emailAddress && normalizeEmailAddress(details.emailAddress);
    const hashedEmailAddress = emailAddress && await hashSha256(emailAddress);
    const mobileNumber = details.mobileNumber && CommonHelpers.normalizeMobileNumberToE164(details.mobileNumber);
    const postcode = details.postcode && normalizePostcode(details.postcode);
    const attributes = {
        ...(emailAddress && { emailAddress }),
        ...(hashedEmailAddress && { hashedEmailAddress }),
        ...(mobileNumber && { mobileNumber }),
        ...(postcode && { postcode })
    };
    if (Object.keys(attributes).length === 0) {
        return;
    }
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push({
        user: {
            profile: {
                attributes,
                authentication: {
                    state: "authenticated"
                }
            }
        }
    });
}


/**
 * Kept for existing usages from FAL-43316.
 *
 * Previous implementation pushed only `hashedEmail`.
 * New implementation delegates to the generic customer-details helper.
 */
export function putEmailIntoAdobeDataLayer(email: string): Promise<void> {
    return putCustomerDetailsIntoAdobeDataLayer({
        emailAddress: email
    });
}
